import { useEffect, useRef } from 'react';

import ProductTypeClass from '../../../types/ProductTypeClass';
import RequestState from '../../../types/RequestState';
import { consoleErrorUnlessFiltered } from '../../../util/sentryFilters';

const isEligibleForStoreAvailability = ({
  product,
  activeProductId,
  configStore,
  accountStore,
}) => {
  if (!product || !activeProductId) {
    return false;
  }

  if (!configStore?.product?.showStoreAvailability) {
    return false;
  }

  if (accountStore?.isViewOnly) {
    return false;
  }

  if (product.product_type === ProductTypeClass.GIFT_VOUCHER) {
    return false;
  }

  return true;
};

/**
 * Prepare store-availability data during render (SSR + client), independent of
 * whether the availability tab is open. Network ownership stays in FullProduct
 * and StorageStore. A client page-entry effect refreshes cached shopping-center
 * metadata; initial hydration and data already loading during render are reused.
 */
const useProductStoreAvailability = ({
  product,
  activeProductId,
  configStore,
  accountStore,
  storageStore,
}) => {
  const eligible = isEligibleForStoreAvailability({
    product,
    activeProductId,
    configStore,
    accountStore,
  });
  const isShoppingCenter = !!configStore?.siteConfig?.isShoppingCenter;
  const merchantUrl = product?.merchantSiteUrl;
  const pageKey = JSON.stringify([
    product?.id,
    activeProductId,
    merchantUrl || null,
    eligible,
    isShoppingCenter,
  ]);
  const entryRef = useRef(null);

  if (
    !entryRef.current ||
    entryRef.current.key !== pageKey ||
    entryRef.current.storageStore !== storageStore
  ) {
    // Capture readiness before render-time preparation starts a request. If
    // this entry already needs loading, the client effect must not fetch again.
    const hadCachedData =
      (storageStore?.state === RequestState.LOADED ||
        storageStore?.state === RequestState.ERROR) &&
      (storageStore.loadedMerchantUrl || null) === (merchantUrl || null);

    entryRef.current = {
      key: pageKey,
      storageStore,
      merchantUrl,
      refresh: eligible && isShoppingCenter && hadCachedData,
    };
  }

  const entry = entryRef.current;
  useEffect(() => {
    if (!entry.storageStore) {
      return;
    }

    entry.storageStore
      .refreshStoragesOnPageEntry(entry.merchantUrl, {
        isHydration: typeof window !== 'undefined' && !!window.isSSR,
        refresh: entry.refresh,
      })
      .catch((e) => {
        consoleErrorUnlessFiltered(e);
      });
  }, [entry]);

  if (!eligible) {
    return;
  }

  storageStore.makeSureStoragesLoaded(product.merchantSiteUrl).catch((e) => {
    consoleErrorUnlessFiltered(e);
  });

  const stockState = product.stockStates.get(activeProductId);
  if (stockState !== RequestState.LOADED && stockState !== RequestState.ERROR) {
    product.loadStocks(activeProductId).catch((e) => {
      consoleErrorUnlessFiltered(e);
    });
  }
};

export default useProductStoreAvailability;
export { isEligibleForStoreAvailability };
